import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DocumentUploadStep = ({ data, registrationData, onComplete, onBack, onCancel, loading }) => {
  const [documents, setDocuments] = useState(data || []);
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);

  const requiredDocuments = [
    {
      id: 'citizenship',
      name: 'PNG Citizenship Certificate or Passport',
      description: 'Valid identification document',
      required: true
    },
    {
      id: 'employment_contract',
      name: 'Employment Contract',
      description: 'Signed employment agreement',
      required: true
    },
    {
      id: 'tin_certificate',
      name: 'TIN Certificate',
      description: 'Tax Identification Number certificate',
      required: false
    },
    {
      id: 'bank_details',
      name: 'Bank Account Details',
      description: 'Bank statement or account verification',
      required: false
    }
  ];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e, documentType) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    const files = e?.dataTransfer?.files;
    if (files && files?.length > 0) {
      handleFiles(files, documentType);
    }
  };

  const handleFileInput = (e, documentType) => {
    const files = e?.target?.files;
    if (files && files?.length > 0) {
      handleFiles(files, documentType);
    }
  };

  const handleFiles = (files, documentType) => {
    const file = files?.[0];
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes?.includes(file?.type)) {
      setErrors(prev => ({
        ...prev,
        [documentType]: 'Only PDF, JPG, and PNG files are allowed'
      }));
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file?.size > maxSize) {
      setErrors(prev => ({
        ...prev,
        [documentType]: 'File size must be less than 5MB'
      }));
      return;
    }

    // Add document to list
    const newDocument = {
      id: Date.now(),
      type: documentType,
      file: file,
      name: file?.name,
      size: file?.size,
      uploadedAt: new Date()?.toISOString()
    };

    setDocuments(prev => {
      // Remove existing document of same type
      const filtered = prev?.filter(doc => doc?.type !== documentType);
      return [...filtered, newDocument];
    });

    // Clear error for this document type
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors?.[documentType];
      return newErrors;
    });
  };

  const handleRemoveDocument = (documentId) => {
    setDocuments(prev => prev?.filter(doc => doc?.id !== documentId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const getDocumentIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    if (extension === 'pdf') return 'FileText';
    if (['jpg', 'jpeg', 'png']?.includes(extension)) return 'Image';
    return 'File';
  };

  const validateForm = () => {
    const newErrors = {};

    requiredDocuments?.forEach(docType => {
      if (docType?.required) {
        const hasDocument = documents?.some(doc => doc?.type === docType?.id);
        if (!hasDocument) {
          newErrors[docType?.id] = `${docType?.name} is required`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onComplete?.(documents);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Document Upload</h2>
        <p className="text-gray-600">
          Upload required documents for {registrationData?.personalDetails?.fullName || 'employee'}'s NASFUND registration
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Document Upload Sections */}
        {requiredDocuments?.map((docType) => {
          const uploadedDoc = documents?.find(doc => doc?.type === docType?.id);
          
          return (
            <div key={docType?.id} className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {docType?.name}
                    {docType?.required && <span className="text-red-600 ml-1">*</span>}
                  </h3>
                  <p className="text-sm text-gray-600">{docType?.description}</p>
                </div>
              </div>

              {uploadedDoc ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Icon name={getDocumentIcon(uploadedDoc?.name)} size={24} className="text-emerald-600 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">{uploadedDoc?.name}</p>
                        <p className="text-sm text-gray-600">{formatFileSize(uploadedDoc?.size)}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDocument(uploadedDoc?.id)}
                      className="text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Icon name="Trash2" size={18} />
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      dragActive
                        ? 'border-blue-500 bg-blue-50'
                        : errors?.[docType?.id]
                        ? 'border-red-300 bg-red-50' :'border-gray-300 hover:border-gray-400'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={(e) => handleDrop(e, docType?.id)}
                  >
                    <Icon name="Upload" size={32} className="text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-700 mb-2">
                      Drag and drop file here, or click to browse
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      PDF, JPG, PNG (max 5MB)
                    </p>
                    <input
                      type="file"
                      id={`file-${docType?.id}`}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileInput(e, docType?.id)}
                    />
                    <label htmlFor={`file-${docType?.id}`}>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById(`file-${docType?.id}`)?.click()}
                      >
                        <Icon name="Upload" size={16} className="mr-2" />
                        Choose File
                      </Button>
                    </label>
                  </div>
                  {errors?.[docType?.id] && (
                    <p className="text-sm text-red-600 mt-2">
                      <Icon name="AlertCircle" size={14} className="inline mr-1" />
                      {errors?.[docType?.id]}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Upload Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="Info" size={20} className="text-blue-600 mr-3 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900 mb-1">Document Requirements</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• All documents must be clear and legible</li>
                <li>• Accepted formats: PDF, JPG, PNG (max 5MB per file)</li>
                <li>• Documents marked with * are required for NASFUND registration</li>
                <li>• Ensure all personal information matches the details provided</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Registration Summary */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Registration Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Employee Name</p>
              <p className="font-medium text-gray-900">{registrationData?.personalDetails?.fullName}</p>
            </div>
            <div>
              <p className="text-gray-600">TIN Number</p>
              <p className="font-medium text-gray-900">{registrationData?.personalDetails?.tinNumber}</p>
            </div>
            <div>
              <p className="text-gray-600">Position</p>
              <p className="font-medium text-gray-900">{registrationData?.employmentInfo?.positionTitle}</p>
            </div>
            <div>
              <p className="text-gray-600">Start Date</p>
              <p className="font-medium text-gray-900">{registrationData?.employmentInfo?.employmentStartDate}</p>
            </div>
            <div>
              <p className="text-gray-600">Beneficiaries</p>
              <p className="font-medium text-gray-900">{registrationData?.beneficiaries?.length || 0} added</p>
            </div>
            <div>
              <p className="text-gray-600">Documents</p>
              <p className="font-medium text-gray-900">{documents?.length} uploaded</p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={loading}
          >
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex-1"
            disabled={loading}
          >
            {loading ? (
              <>
                <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                Submitting Registration...
              </>
            ) : (
              <>
                <Icon name="CheckCircle" size={16} className="mr-2" />
                Complete NASFUND Registration
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DocumentUploadStep;